/**
 * managementTabs.js
 *
 * Single source of truth for the Management bottom-tab metadata (route name,
 * access key, i18n label + fallback, icon, Mail gating). Both the navigator
 * ({@link module:screens/ManagementHomeScreen}) and the "Main screen" picker in
 * {@link module:screens/ManagementSettingsScreen} build their tab list from this
 * array so the two stay perfectly in sync (same order, same gating).
 *
 * Only metadata lives here — the heavy screen components are wired up inside the
 * navigator via a name→component map, keeping this config lazy-import-free.
 */

/**
 * @typedef {object} ManagementTabMeta
 * @property {string} name       React Navigation route name (also the persisted home/last-tab value).
 * @property {string} [accessKey] managementAccess key; the tab is shown only when `canAccess(accessKey).allowed`.
 * @property {boolean} [mailGated] When true, the tab is shown only when a mailbox is configured (accessKey ignored).
 * @property {string} labelKey   i18n key for the tab label.
 * @property {string} fallback   Hungarian fallback label when the i18n key is missing.
 * @property {string} icon       MaterialCommunityIcons glyph name.
 */

/**
 * The 5-tab manager bottom bar: Dashboard · Modules · Mail · Calendar · Settings.
 * Finance and Service intentionally have NO tab — they live in the Modules hub
 * (ManagementDataScreen) and open as stack routes (ManagementFinanceScreen /
 * ManagementServiceScreen). Mail is a permanent tab (no mailbox gating): with no
 * configured account, MailHomeScreen renders its own account-wizard onboarding.
 * @type {ManagementTabMeta[]}
 */
export const MANAGEMENT_TABS = [
    {name: 'ManagementDashboard', accessKey: 'dashboard', labelKey: 'management.dashboard', fallback: 'Vezetői', icon: 'view-dashboard-outline'},
    {name: 'ManagementData', accessKey: 'data', labelKey: 'management.modules', fallback: 'Modulok', icon: 'view-grid-outline'},
    {name: 'ManagementMail', accessKey: 'dashboard', labelKey: 'mail_title', fallback: 'Mail', icon: 'email-outline'},
    {name: 'ManagementCalendar', accessKey: 'dashboard', labelKey: 'calendar_title', fallback: 'Naptár', icon: 'calendar-month-outline'},
    {name: 'ManagementSettings', accessKey: 'settings', labelKey: 'management.settings', fallback: 'Beállítások', icon: 'cog-outline'},
];

/**
 * Resolve a tab's localized label, falling back to its Hungarian default.
 * @param {Function} t   react-i18next translate function.
 * @param {ManagementTabMeta} tab
 * @returns {string}
 */
export const resolveManagementTabLabel = (t, tab) => (tab ? (t(tab.labelKey) || tab.fallback) : '');

/**
 * Compute the access-filtered, feature-gated list of Management tabs — the exact
 * set the bottom-tab bar renders. Every tab requires its managementAccess key to
 * be allowed. (Mail is no longer mailbox-gated — it is a permanent tab and shows
 * its own onboarding when no account is configured; the `mailGated` branch stays
 * for any future opt-in tab.)
 * @param {object} params
 * @param {Function} params.canAccess       `useManagementAccess().canAccess` — `(key) => ({ allowed })`.
 * @param {boolean} [params.mailConfigured] Whether a mailbox is set up (from `useMail().configured`).
 * @returns {ManagementTabMeta[]}
 */
export const visibleManagementTabs = ({canAccess, mailConfigured}) => MANAGEMENT_TABS.filter((tab) => {
    if (tab.mailGated) {
        return !!mailConfigured;
    }

    return !!(canAccess && canAccess(tab.accessKey).allowed);
});

export default MANAGEMENT_TABS;
